import { useState } from "react";
import { Box, Button, Card, Dialog, Typography, Tooltip, Stack, IconButton } from "@mui/material";
import { LocationOn, SwapHoriz, LocalOfferOutlined } from "@mui/icons-material";
import { useChatPdVContext } from "../../../../contexts/atendimento/ChatPdVContext";
import { GestaoEnderecosCliente } from "../../../../../clientes/containers/GestaoEnderecosCliente";
import { AlterarPrecoFreteModal } from "../../../AlterarPrecoFreteModal";
import { alpha } from "@mui/material/styles";
import { acaoPrincipal, contorno, cores, raio, superficie } from "../../identidade";

export const ChatPdVEndereco = () => {
    const {
        cliente,
        endereco,
        vincularEndereco,
        alterarEndereco,
        valorFrete,
        alterarValorFrete,
        loadingVincularEndereco,
        loading
    } = useChatPdVContext();

    const [modalEnderecoAberta, setModalEnderecoAberta] = useState(false);
    const [modalAlterarPrecoAberta, setModalAlterarPrecoAberta] = useState(false);

    const handleAbrirModalEndereco = () => {
        setModalEnderecoAberta(true);
    };

    const handleFecharModalEndereco = () => {
        setModalEnderecoAberta(false);
    };

    const handleVincularEndereco = (enderecoSelecionado: any) => {
        vincularEndereco(enderecoSelecionado);
        handleFecharModalEndereco();
    };

    const handleAlterarEndereco = () => {
        alterarEndereco();
        handleAbrirModalEndereco();
    };

    // Formatar endereço para exibição
    const formatarEndereco = () => {
        if (!endereco) return null;

        let linha1 = endereco.rua ?? "";
        if (endereco.numero) {
            linha1 += ` ${endereco.numero}`;
        }

        let linha2 = "";
        if (endereco.bairro || endereco.cidade) {
            const bairro = endereco.bairro ?? "";
            const cidade = endereco.cidade ?? "";
            linha2 = bairro && cidade ? `${bairro} - ${cidade}` : `${bairro}${cidade}`;
        }

        if (endereco.complemento) {
            linha2 = linha2 ? `${endereco.complemento}, ${linha2}` : endereco.complemento;
        }

        return { linha1, linha2 };
    };

    const enderecoFormatado = formatarEndereco();

    if (!cliente) {
        return null;
    }

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    px: 1.5,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexShrink: 0,
                    borderRadius: raio.md,
                    backgroundColor: theme => superficie(theme, 0.03),
                    border: theme => contorno(theme, 0.08),
                    backgroundImage: 'none',
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(237, 137, 54, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <LocationOn sx={{ color: '#ea580c', fontSize: 24 }} />
                </Box>
                {endereco ? (
                    <>
                        <Tooltip title={`${enderecoFormatado?.linha1} ${enderecoFormatado?.linha2}`.trim()}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    fontWeight={700}
                                    sx={{
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {enderecoFormatado?.linha1}
                                </Typography>
                                <Typography
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '0.8rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        mt: 0.25,
                                    }}
                                >
                                    {enderecoFormatado?.linha2}
                                </Typography>
                            </Box>
                        </Tooltip>
                        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                            <Tooltip title="Trocar endereço">
                                <IconButton
                                    onClick={handleAlterarEndereco}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: raio.sm,
                                        color: cores.marca[500],
                                        backgroundColor: alpha(cores.marca[500], 0.1),
                                        '&:hover': { backgroundColor: alpha(cores.marca[500], 0.18) },
                                    }}
                                >
                                    <SwapHoriz sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Alterar preço do frete">
                                <IconButton
                                    onClick={() => setModalAlterarPrecoAberta(true)}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        color: '#9333ea',
                                        backgroundColor: 'rgba(147, 51, 234, 0.08)',
                                        borderRadius: 2,
                                        '&:hover': {
                                            backgroundColor: 'rgba(147, 51, 234, 0.15)',
                                        },
                                    }}
                                >
                                    <LocalOfferOutlined sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </>
                ) : (
                    <>
                        <Typography color="text.secondary" sx={{ flex: 1, fontSize: '0.8rem', fontStyle: 'italic' }}>
                            Nenhum endereço vinculado
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleAbrirModalEndereco}
                            sx={theme => ({
                                ...acaoPrincipal(theme),
                                fontSize: "12px",
                                borderRadius: raio.sm,
                                flexShrink: 0,
                            })}
                        >
                            Vincular
                        </Button>
                    </>
                )}
            </Card>

            {/* Modal de Gestão de Endereços */}
            <Dialog
                open={modalEnderecoAberta}
                onClose={handleFecharModalEndereco}
                sx={{ zIndex: 10001 }}
            >
                <GestaoEnderecosCliente
                    cliente={cliente}
                    salvar={handleVincularEndereco}
                    cancelar={handleFecharModalEndereco}
                    opcoes={["VINCULAR_ENDERECO", "EDITAR_ENDERECO", "EXCLUIR_ENDERECO"]}
                    loading={loadingVincularEndereco}
                />
            </Dialog>

            {/* Modal de Alterar Preço do Frete */}
            <AlterarPrecoFreteModal
                open={modalAlterarPrecoAberta}
                valorAtual={valorFrete}
                onClose={() => setModalAlterarPrecoAberta(false)}
                onConfirm={(novoValor: number) => {
                    alterarValorFrete(novoValor);
                    setModalAlterarPrecoAberta(false);
                }}
                loading={loading}
            />
        </>
    );
};
